import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CROSS_CHANNEL,
  MetricsTablePreset,
  PerformanceData,
  SavePresetParams as SaveTablePresetParams
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import { PacingOptionsResponse, RatioGoalConflict } from '@blisspointmedia/bpm-types/dist/Pacing';
import {
  DeletePresetParams as DeletePagePresetParams,
  GetPresetsParams as GetPagePresetsParams,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams,
  SavePresetResponse as SavePagePresetResponse
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  SingleChannelLambdaFetch
} from '@src/utils/fetch-utils';
import { parseBase64GzippedResponse } from '@src/utils/date-utils';

// The CrossChannel pacing endpoints treat the tinuititest sandbox as dsw.
const pacingCompany = (company: string): string => (company === 'tinuititest' ? 'dsw' : company);

// --- Server reads (React Query) -------------------------------------------------------------

// Options for every dropdown/picker: goal types, segments-per-goal, source/KPI combos, platforms.
export const usePacingOptions = (
  company: string,
  start: string,
  end: string
): ReturnType<typeof useQuery<PacingOptionsResponse>> =>
  useQuery({
    queryKey: ['pacingOptions', company, start, end],
    enabled: Boolean(company && start && end),
    queryFn: async (): Promise<PacingOptionsResponse> => {
      const res = await CrossChannelLambdaFetch('/getCrossChannelPacingOptionsV2', {
        params: { company: pacingCompany(company), start, end }
      });
      return awaitJSON<PacingOptionsResponse>(res);
    }
  });

// Cross-channel filter categories (pacing reuses the cross-channel dimension map verbatim).
export const usePacingFilterOptions = (
  company: string
): ReturnType<typeof useQuery<CC.GetFilterOptionsResponse>> =>
  useQuery({
    queryKey: ['pacingFilterOptions', company],
    enabled: Boolean(company),
    queryFn: async (): Promise<CC.GetFilterOptionsResponse> => {
      const res = await SingleChannelLambdaFetch('/getChannelFilterSourceOptions', {
        params: { company, channel: CROSS_CHANNEL, pageID: CROSS_CHANNEL }
      });
      const [filterOptions] =
        await parseBase64GzippedResponse<[CC.GetFilterOptionsResponse, unknown, unknown]>(res);
      return filterOptions;
    }
  });

export const useTablePresets = (
  company: string,
  mediatype: string
): ReturnType<typeof useQuery<MetricsTablePreset[]>> =>
  useQuery({
    queryKey: ['pacingTablePresets', company, mediatype],
    enabled: Boolean(company),
    queryFn: async (): Promise<MetricsTablePreset[]> => {
      const res = await SingleChannelLambdaFetch('/metrics_table_presets', {
        params: { company, mediatype }
      });
      const parsed = await awaitJSON<MetricsTablePreset[] | { errorMessage: string }>(res);
      return Array.isArray(parsed) ? parsed : [];
    }
  });

export const usePagePresets = (
  company: string,
  mediatype: string
): ReturnType<typeof useQuery<MetricsPagePreset[]>> =>
  useQuery({
    queryKey: ['pacingPagePresets', company, mediatype],
    enabled: Boolean(company),
    queryFn: async (): Promise<MetricsPagePreset[]> => {
      const res = await SingleChannelLambdaFetch<GetPagePresetsParams>('/metrics_page_presets', {
        params: { company, mediatype }
      });
      const parsed = await awaitJSON<MetricsPagePreset[] | { errorMessage: string }>(res);
      return Array.isArray(parsed) ? parsed : [];
    }
  });

export interface PacingBarsParams {
  company: string;
  goal: string;
  activeDimensions: string;
  start: string;
  end: string;
  kpi: string;
  sources: string;
}

export interface PacingBarsResult {
  pacingBarsData: Record<string, any>;
}

// The whole bars pipeline (goals -> actuals -> attribution -> math) in one call. React Query cancels
// a superseded request when the query key changes, so a stale response can't overwrite a newer one.
export const usePacingBars = (
  params: PacingBarsParams,
  enabled: boolean
): ReturnType<typeof useQuery<PacingBarsResult>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['pacingBars', params],
    enabled,
    // Keep the previous bars on screen (under a LoadingOverlay) while a new goal/dimension/date
    // range loads, instead of unmounting the dashboard controls.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PacingBarsResult> => {
      const res = await CrossChannelLambdaFetch('/getCrossChannelPacingBarsV2', {
        params: { ...params, company: pacingCompany(params.company) }
      });
      const data = await awaitJSON<PacingBarsResult>(res);
      return { pacingBarsData: data?.pacingBarsData ?? {} };
    }
  });

export interface PacingTableParams {
  company: string;
  start: string;
  end: string;
  activeDimensions: string;
  kpi: string;
  sources: string;
  goalMetrics: string;
  filterID?: number;
  filterState?: string;
}

export interface PacingTableResult {
  summary: PerformanceData[];
  overTime: PerformanceData[];
  conflicts?: RatioGoalConflict[];
}

// One call returns both the summary and over-time rows (Simple + Adjusted values per row), so the
// view/mode toggles are client-side.
export const usePacingTableData = (
  params: PacingTableParams,
  enabled: boolean
): ReturnType<typeof useQuery<PacingTableResult>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['pacingTableData', params],
    enabled,
    queryFn: async (): Promise<PacingTableResult> => {
      const res = await CrossChannelLambdaFetch('/getCrossChannelPacingTableV2', {
        params: { ...params, company: pacingCompany(params.company) }
      });
      return awaitJSON<PacingTableResult>(res);
    }
  });

// --- Imperative writes (plain helpers; callers invalidate the matching query) ----------------

export const getTablePreset = async (params: {
  id: number;
  company: string;
  mediatype: string;
}): Promise<MetricsTablePreset> => {
  const res = await SingleChannelLambdaFetch('/metrics_table_preset', { params });
  return awaitJSON<MetricsTablePreset>(res);
};

// The POST payload for /metrics_table_preset. Reuses the canonical SavePresetParams, but makes
// `preset` optional: the rename path saves id/name/temporary without the full column config.
export type SaveTablePresetBody = Omit<SaveTablePresetParams, 'preset'> & {
  preset?: MetricsTablePreset;
};

export const saveTablePreset = async (
  body: SaveTablePresetBody
): Promise<{ id: number; name: string }> => {
  const res = await SingleChannelLambdaFetch('/metrics_table_preset', { method: 'POST', body });
  return awaitJSON<{ id: number; name: string }>(res);
};

export const deleteTablePreset = async (params: {
  id: number;
  mediatype: string;
}): Promise<void> => {
  const res = await SingleChannelLambdaFetch('/metrics_table_preset', { method: 'DELETE', params });
  await awaitJSON(res);
};

export const savePagePreset = async (
  body: SavePagePresetParams
): Promise<SavePagePresetResponse> => {
  const res = await SingleChannelLambdaFetch<SavePagePresetParams>('/metrics_page_preset', {
    method: 'POST',
    body
  });
  return awaitJSON<SavePagePresetResponse>(res);
};

export const deletePagePreset = async (params: DeletePagePresetParams): Promise<void> => {
  const res = await SingleChannelLambdaFetch<DeletePagePresetParams>('/metrics_page_preset', {
    method: 'DELETE',
    params
  });
  await awaitJSON(res);
};
